import { defaultPlanValue } from '../stores/create-plan-table.store';

import { convertFromTableData } from './use-excel-table';
import { getDefaultPlanData } from './use-excel-table';

import type { PlanTableData, PlanTableRow } from '../components/common/handsontable.types';
import type {
  UpdatePlanRequest,
  CreatePlanRequest,
  PropertyPlansApiResponse,
} from '../types/plan.types';
import type { SearchOption } from '@/types/search';

type OriginalPlanMapEntry = {
  index: number;
  displayOrder?: number;
  plan: CreatePlanRequest;
};

type PlanDeliveryWithDisplayOrder = CreatePlanRequest & { displayOrder?: number };

const getCellString = (row: PlanTableRow, columnIndex: number): string => {
  const value = row[columnIndex];
  return value == null ? '' : String(value);
};

const DEFAULT_CREATE_ROW = getDefaultPlanData(0, defaultPlanValue, [], []);
const DEFAULT_CREATE_ROW_WITH_EMPTY_DATE = [...DEFAULT_CREATE_ROW];
DEFAULT_CREATE_ROW_WITH_EMPTY_DATE[2] = '';

export const isUntouchedBlankCreateRow = (row: PlanTableRow): boolean => {
  const comparableRow = row.slice(0, DEFAULT_CREATE_ROW.length);
  return (
    JSON.stringify(comparableRow) === JSON.stringify(DEFAULT_CREATE_ROW) ||
    JSON.stringify(comparableRow) === JSON.stringify(DEFAULT_CREATE_ROW_WITH_EMPTY_DATE)
  );
};

/**
 * Analyze tableData and originalTableData to categorize records into:
 * - Updated records: Records that exist in both but have been modified
 * - New records: Records that don't exist in original (no id or empty id)
 * - Deleted records: Records that exist in original but not in current tableData
 *
 * @param tableData - Current table data after user edits
 * @param originalTableData - Original table data when page loaded
 * @returns Object containing categorized table data arrays
 */
export const handleGetCreateUpdateOrDeletedPlanRecords = (
  tableData: PlanTableData,
  originalTableData: PlanTableData
): {
  tableUpdateData: PlanTableData;
  tableCreateNewData: PlanTableData;
  tableDeletedData: PlanTableData;
} => {
  const ID_COLUMN_INDEX = 23; // Index where plan.id is stored when isDetail = true
  const IS_DELETED_COLUMN_INDEX = 24; // Index where isDeleted flag is stored

  // Create a map of original records by id for quick lookup
  // IMPORTANT: Store a deep copy of each row to prevent mutation
  // This ensures that originalRow values (like 可能時間補助 at index 5) are preserved correctly
  // Use JSON parse/stringify for true deep copy to prevent any reference issues
  const originalRecordsMap = new Map<string, { row: PlanTableRow; index: number }>();
  originalTableData.forEach((row, index) => {
    const id = getCellString(row, ID_COLUMN_INDEX);
    if (id !== '') {
      // Create a deep copy of the row to prevent any potential mutation
      // Using spread operator only does shallow copy, which can cause issues
      const rowCopy = JSON.parse(JSON.stringify(row));
      originalRecordsMap.set(id, { row: rowCopy, index });
    }
  });

  // Track which original records are found in current tableData (not deleted)
  const foundOriginalIds = new Set<string>();
  // Track deleted record ids to avoid duplicates
  const deletedIds = new Set<string>();

  const tableUpdateData: PlanTableData = [];
  const tableCreateNewData: PlanTableData = [];
  const tableDeletedData: PlanTableData = [];

  // Process current tableData
  tableData.forEach((row) => {
    const id = getCellString(row, ID_COLUMN_INDEX);
    const isDeleted = row[IS_DELETED_COLUMN_INDEX] === 'TRUE';

    // If row is marked as deleted and has an id, add to deleted list
    if (isDeleted && id !== '') {
      deletedIds.add(id);
      // Create a deep copy to prevent mutation
      const deletedRowCopy = JSON.parse(JSON.stringify(row));
      tableDeletedData.push(deletedRowCopy);
      return;
    }

    // If row has no id or empty id, it's a new record
    if (!id || id === '') {
      // Ignore untouched blank rows (default rows appended for edit UX)
      if (isUntouchedBlankCreateRow(row)) {
        return;
      }

      // Create a deep copy to prevent mutation
      const newRowCopy = JSON.parse(JSON.stringify(row));
      tableCreateNewData.push(newRowCopy);
    } else {
      const originalRecord = originalRecordsMap.get(id);
      if (originalRecord) {
        // Record exists in original - check if it's been modified
        const originalRow = originalRecord.row;
        foundOriginalIds.add(id);

        // Compare rows to see if data has changed
        // Exclude the id, isDeleted, and isArchive columns from comparison (last 3 columns)
        const currentRowForComparison = row.slice(0, ID_COLUMN_INDEX);
        const originalRowForComparison = originalRow.slice(0, ID_COLUMN_INDEX);

        // Check if any field has changed
        const hasChanged =
          JSON.stringify(currentRowForComparison) !== JSON.stringify(originalRowForComparison);

        if (hasChanged) {
          // Push original row first, then updated row (xen kẽ)
          // IMPORTANT: Create a deep copy of originalRow to prevent mutation
          // Use JSON parse/stringify for true deep copy to ensure all values are preserved correctly
          // This is especially important for columns like 可能時間補助 (index 5) that might be mutated elsewhere
          // Using spread operator or map() only does shallow copy, which can cause issues if originalTableData was mutated
          const originalRowCopy = JSON.parse(JSON.stringify(originalRow)); // True deep copy
          const updatedRowCopy = JSON.parse(JSON.stringify(row)); // True deep copy

          tableUpdateData.push(originalRowCopy); // Original row (will be disabled) - deep copy
          tableUpdateData.push(updatedRowCopy); // Updated row (will have yellow background and red text) - deep copy
        }
      } else {
        // Record has id but not in original - treat as new (shouldn't happen normally)
        tableCreateNewData.push(row);
      }
    }
  });

  // Find deleted records: records in original but not found in current tableData
  // (excluding those already marked as deleted in current tableData)
  originalTableData.forEach((row) => {
    const id = getCellString(row, ID_COLUMN_INDEX);
    if (id !== '' && !foundOriginalIds.has(id) && !deletedIds.has(id)) {
      // Create a deep copy to prevent mutation
      const deletedRowCopy = JSON.parse(JSON.stringify(row));
      tableDeletedData.push(deletedRowCopy);
    }
  });

  return {
    tableUpdateData,
    tableCreateNewData,
    tableDeletedData,
  };
};

/**
 * Build a map of updated rows by ID from tableUpdateData
 * Handles both 26-column rows (with ID) and 23-column rows (need to match with tableData)
 *
 * @param tableUpdateData - Updated rows from confirm view (pairs: [original, updated, ...])
 * @param tableData - Current table data in edit view (for matching 23-column rows)
 * @param ID_COLUMN_INDEX - Index where plan.id is stored
 * @returns Map of updated rows by ID
 */
const buildUpdatedRowsMap = (
  tableUpdateData: PlanTableData,
  tableData: PlanTableData,
  ID_COLUMN_INDEX: number
): Map<string, PlanTableRow> => {
  const updatedRowsMap = new Map<string, PlanTableRow>();

  tableUpdateData.forEach((row, index) => {
    if (index % 2 === 1) {
      // This is an updated row (odd index)
      let rowId: string | undefined;
      let fullRow: PlanTableRow = [...row];

      // Check if row has ID column (26 columns) or not (23 columns)
      if (row.length >= 26 && getCellString(row, ID_COLUMN_INDEX) !== '') {
        // Row has full 26 columns with ID
        rowId = getCellString(row, ID_COLUMN_INDEX);
      } else if (row.length === 23) {
        // Row has only 23 columns (from Handsontable edit in confirm view)
        // Need to match with tableData to get ID
        // Try to find matching row in tableData by key fields
        // Use flexible matching: match by placementDate (2), timePossible (4), placeToPlace (6), quantity (7)
        const matchingRow = tableData.find((tRow) => {
          if (tRow.length < 23) return false;
          // Match by key fields - allow some flexibility since user might have changed some fields
          const placementDateMatch = tRow[2] === row[2]; // placementDate
          const timePossibleMatch = tRow[4] === row[4]; // timePossible
          const placeToPlaceMatch = tRow[6] === row[6]; // placeToPlace
          const quantityMatch = tRow[7] === row[7]; // quantity

          // At least 2 fields should match (to handle cases where user changed some fields)
          const matchCount = [
            placementDateMatch,
            timePossibleMatch,
            placeToPlaceMatch,
            quantityMatch,
          ].filter(Boolean).length;

          return matchCount >= 2; // At least 2 fields match
        });

        if (
          matchingRow &&
          matchingRow.length >= 26 &&
          getCellString(matchingRow, ID_COLUMN_INDEX) !== ''
        ) {
          rowId = getCellString(matchingRow, ID_COLUMN_INDEX);
          fullRow = [
            ...row, // First 23 columns from updated row
            rowId || '', // id
            matchingRow[24] || '0', // isDeleted
            matchingRow[25] || '0', // isArchive
          ];
        }
      }

      if (rowId && rowId !== '') {
        updatedRowsMap.set(rowId, fullRow);
      }
    }
  });

  return updatedRowsMap;
};

/**
 * First pass: Sync updated rows by matching ID
 *
 * @param syncedTableData - Table data to sync (will be modified)
 * @param updatedRowsMap - Map of updated rows by ID
 * @param ID_COLUMN_INDEX - Index where plan.id is stored
 * @returns Whether any data was changed
 */
const syncUpdatedRows = (
  syncedTableData: PlanTableData,
  updatedRowsMap: Map<string, PlanTableRow>,
  ID_COLUMN_INDEX: number
): boolean => {
  let hasDataChanged = false;

  syncedTableData.forEach((row, index) => {
    const id = getCellString(row, ID_COLUMN_INDEX);
    if (id === '') return;

    const updatedRow = updatedRowsMap.get(id);
    if (updatedRow) {
      // Always sync updated rows from confirm view - they represent the latest state
      // Replace ENTIRE row with latest from confirm view (all 26 columns)
      syncedTableData[index] = [...updatedRow];
      hasDataChanged = true;
    }
  });

  return hasDataChanged;
};

/**
 * Second pass: Sync create rows by matching position and content
 *
 * @param syncedTableData - Table data to sync (will be modified)
 * @param tableCreateNewData - New rows from confirm view
 * @param ID_COLUMN_INDEX - Index where plan.id is stored
 * @returns Object containing matched indices, index map, and whether any data was changed
 */
const syncCreateRows = (
  syncedTableData: PlanTableData,
  tableCreateNewData: PlanTableData,
  ID_COLUMN_INDEX: number
): {
  matchedCreateRowIndices: Set<number>;
  createRowIndexMap: Map<number, number>;
  hasDataChanged: boolean;
} => {
  const matchedCreateRowIndices = new Set<number>();
  const createRowIndexMap = new Map<number, number>(); // Map from tableCreateNewData index to syncedTableData index
  let hasDataChanged = false;

  // Count create rows in tableData to determine matching strategy
  const createRowsInTableData = syncedTableData.filter(
    (row) => getCellString(row, ID_COLUMN_INDEX) === ''
  );

  // If counts match, try position-based matching first (most reliable)
  let createRowIndex = 0;
  syncedTableData.forEach((row, tableDataIndex) => {
    const id = getCellString(row, ID_COLUMN_INDEX);
    if (id === '') {
      // This is a create row
      let matchedIndex = -1;

      // First, try position-based matching (if order hasn't changed)
      if (
        createRowIndex < tableCreateNewData.length &&
        createRowsInTableData.length === tableCreateNewData.length
      ) {
        // Try matching by position first
        const candidateRow = tableCreateNewData[createRowIndex];
        // Quick check: if at least one key field matches, consider it a match
        const keyFields = [2, 4, 6, 7]; // placementDate, timePossible, placeToPlace, quantity
        const hasAnyMatch = keyFields.some((fieldIndex) => {
          const rowValue = row[fieldIndex] != null ? String(row[fieldIndex]).trim() : '';
          const candidateValue =
            candidateRow[fieldIndex] != null ? String(candidateRow[fieldIndex]).trim() : '';
          return rowValue === candidateValue && rowValue !== '';
        });

        if (hasAnyMatch && !matchedCreateRowIndices.has(createRowIndex)) {
          matchedIndex = createRowIndex;
        }
      }

      // If position-based matching failed, try content-based matching
      if (matchedIndex === -1) {
        let bestMatchIndex = -1;
        let bestMatchScore = 0;

        // Find the best matching create row from confirm view
        tableCreateNewData.forEach((createRow, createIndex) => {
          // Skip if already matched
          if (matchedCreateRowIndices.has(createIndex)) return;

          // Calculate match score based on key fields
          let matchScore = 0;
          const keyFields = [2, 4, 6, 7]; // placementDate, timePossible, placeToPlace, quantity
          keyFields.forEach((fieldIndex) => {
            const rowValue = row[fieldIndex] != null ? String(row[fieldIndex]).trim() : '';
            const createValue =
              createRow[fieldIndex] != null ? String(createRow[fieldIndex]).trim() : '';
            if (rowValue === createValue && rowValue !== '') {
              matchScore++;
            }
          });

          // If this is a better match (more fields match), update best match
          if (matchScore > bestMatchScore) {
            bestMatchScore = matchScore;
            bestMatchIndex = createIndex;
          }
        });

        if (bestMatchIndex !== -1) {
          matchedIndex = bestMatchIndex;
        }
      }

      // If we found a match, sync the row
      if (matchedIndex !== -1) {
        const createRow = tableCreateNewData[matchedIndex];
        // Always sync - replace ENTIRE row with latest from confirm view (all 26 columns)
        // This captures ALL changes made in confirm page, including 打設日 (placementDate)
        syncedTableData[tableDataIndex] = [...createRow];
        matchedCreateRowIndices.add(matchedIndex);
        // Map createRowIndex in tableCreateNewData to tableDataIndex in syncedTableData for error mapping
        createRowIndexMap.set(matchedIndex, tableDataIndex);
        hasDataChanged = true;
      }

      createRowIndex++;
    }
  });

  return { matchedCreateRowIndices, createRowIndexMap, hasDataChanged };
};

/**
 * Third pass: Add any additional create rows that weren't matched
 *
 * @param syncedTableData - Table data to sync (will be modified)
 * @param tableCreateNewData - New rows from confirm view
 * @param matchedCreateRowIndices - Set of indices that have already been matched
 * @param createRowIndexMap - Map from tableCreateNewData index to syncedTableData index (will be updated)
 * @returns Whether any data was changed
 */
const addUnmatchedCreateRows = (
  syncedTableData: PlanTableData,
  tableCreateNewData: PlanTableData,
  matchedCreateRowIndices: Set<number>,
  createRowIndexMap: Map<number, number>
): boolean => {
  let hasDataChanged = false;

  tableCreateNewData.forEach((createRow, createIndex) => {
    if (!matchedCreateRowIndices.has(createIndex)) {
      // This create row wasn't matched - it's a new row added in confirm view
      const newIndex = syncedTableData.length;
      syncedTableData.push([...createRow]);
      // Map createRowIndex to the new syncedTableData index for error mapping
      createRowIndexMap.set(createIndex, newIndex);
      hasDataChanged = true;
    }
  });

  return hasDataChanged;
};

/**
 * Match a 23-column row with tableData to find the corresponding ID
 * Uses flexible matching: at least 2 key fields must match
 *
 * @param updatedRow - Row with 23 columns from confirm view
 * @param tableData - Original table data in edit view
 * @param ID_COLUMN_INDEX - Index where plan.id is stored
 * @returns Row ID if match found, undefined otherwise
 */
const findRowIdByMatching = (
  updatedRow: PlanTableRow,
  tableData: PlanTableData,
  ID_COLUMN_INDEX: number
): string | undefined => {
  const matchingRow = tableData.find((tRow) => {
    if (tRow.length < 23) return false;
    const placementDateMatch = tRow[2] === updatedRow[2];
    const timePossibleMatch = tRow[4] === updatedRow[4];
    const placeToPlaceMatch = tRow[6] === updatedRow[6];
    const quantityMatch = tRow[7] === updatedRow[7];
    const matchCount = [
      placementDateMatch,
      timePossibleMatch,
      placeToPlaceMatch,
      quantityMatch,
    ].filter(Boolean).length;
    return matchCount >= 2;
  });

  if (matchingRow && matchingRow.length >= 26 && matchingRow[ID_COLUMN_INDEX]) {
    return getCellString(matchingRow, ID_COLUMN_INDEX);
  }

  return undefined;
};

/**
 * Get row ID from updated row (handles both 26-column and 23-column rows)
 *
 * @param updatedRow - Updated row from confirm view
 * @param tableData - Original table data in edit view (for matching 23-column rows)
 * @param ID_COLUMN_INDEX - Index where plan.id is stored
 * @returns Row ID if found, undefined otherwise
 */
const getRowIdFromUpdatedRow = (
  updatedRow: PlanTableRow,
  tableData: PlanTableData,
  ID_COLUMN_INDEX: number
): string | undefined => {
  // Check if updatedRow has ID (26 columns) or needs to be matched
  if (updatedRow.length >= 26 && updatedRow[ID_COLUMN_INDEX]) {
    return getCellString(updatedRow, ID_COLUMN_INDEX);
  }

  if (updatedRow.length === 23) {
    // Match with tableData to get ID
    return findRowIdByMatching(updatedRow, tableData, ID_COLUMN_INDEX);
  }

  return undefined;
};

/**
 * Map update section error to syncedTableData index
 *
 * @param error - Error from update section
 * @param tableUpdateData - Updated rows from confirm view
 * @param tableData - Original table data in edit view
 * @param syncedTableDataMap - Map of syncedTableData by ID
 * @param ID_COLUMN_INDEX - Index where plan.id is stored
 * @returns SyncedTableData index if found, undefined otherwise
 */
const mapUpdateSectionError = (
  error: { rowIndex: number; field: { index: number; message: string }[] },
  tableUpdateData: PlanTableData,
  tableData: PlanTableData,
  syncedTableDataMap: Map<string, number>,
  ID_COLUMN_INDEX: number
): number | undefined => {
  const isUpdatedRow = error.rowIndex % 2 === 1;
  if (!isUpdatedRow) {
    return undefined;
  }

  const updatedRow = tableUpdateData[error.rowIndex];
  if (!updatedRow) {
    return undefined;
  }

  const rowId = getRowIdFromUpdatedRow(updatedRow, tableData, ID_COLUMN_INDEX);
  if (!rowId) {
    return undefined;
  }

  return syncedTableDataMap.get(rowId);
};

/**
 * Parameters for merging errors from confirm view
 */
interface MergeErrorsParams {
  syncedTableData: PlanTableData;
  tableData: PlanTableData;
  tableUpdateData: PlanTableData;
  tableCreateNewData: PlanTableData;
  updateSectionErrors: { rowIndex: number; field: { index: number; message: string }[] }[];
  createSectionErrors: { rowIndex: number; field: { index: number; message: string }[] }[];
  rowsWithErrors: { rowIndex: number; field: { index: number; message: string }[] }[];
  createRowIndexMap: Map<number, number>;
  ID_COLUMN_INDEX: number;
}

/**
 * Map create section error to syncedTableData index with fallback
 *
 * @param error - Error from create section
 * @param createRow - Create row from tableCreateNewData
 * @param createRowIndexMap - Map from tableCreateNewData index to syncedTableData index
 * @param syncedTableData - Synced table data
 * @param ID_COLUMN_INDEX - Index where plan.id is stored
 * @returns SyncedTableData index if found, undefined otherwise
 */
const mapCreateSectionError = (
  error: { rowIndex: number; field: { index: number; message: string }[] },
  createRow: PlanTableRow,
  createRowIndexMap: Map<number, number>,
  syncedTableData: PlanTableData,
  ID_COLUMN_INDEX: number
): number | undefined => {
  // Use the map created during sync to find the corresponding syncedTableData index
  const tableDataIndex = createRowIndexMap.get(error.rowIndex);

  if (tableDataIndex !== undefined) {
    return tableDataIndex;
  }

  // Fallback: if map doesn't have this index (shouldn't happen), try content-based matching
  // This handles edge cases where mapping might have failed
  const fallbackIndex = syncedTableData.findIndex((row) => {
    if (getCellString(row, ID_COLUMN_INDEX) === '') {
      // Match by content for new rows
      const matchingFields = [
        row[2] === createRow[2], // placementDate
        row[4] === createRow[4], // timePossible
        row[6] === createRow[6], // placeToPlace
        row[7] === createRow[7], // quantity
      ].filter(Boolean).length;
      return matchingFields >= 2;
    }
    return false;
  });

  return fallbackIndex !== -1 ? fallbackIndex : undefined;
};

/**
 * Merge errors from confirm view back to main errors
 *
 * @param params - Parameters for merging errors
 * @returns Merged errors array
 */
const mergeErrors = (
  params: MergeErrorsParams
): { rowIndex: number; field: { index: number; message: string }[] }[] => {
  const {
    syncedTableData,
    tableData,
    tableUpdateData,
    tableCreateNewData,
    updateSectionErrors,
    createSectionErrors,
    rowsWithErrors,
    createRowIndexMap,
    ID_COLUMN_INDEX,
  } = params;

  const mergedErrors: { rowIndex: number; field: { index: number; message: string }[] }[] = [];

  // Create a map of syncedTableData by ID for efficient lookup (for updated rows)
  const syncedTableDataMap = new Map<string, number>();
  syncedTableData.forEach((row, index) => {
    const id = getCellString(row, ID_COLUMN_INDEX);
    if (id !== '') {
      syncedTableDataMap.set(id, index);
    }
  });

  // Map update section errors: each pair has 2 rows (original + updated)
  // Only updated rows (odd indices) can have errors, map them back to synced tableData indices
  updateSectionErrors.forEach((error) => {
    const tableDataIndex = mapUpdateSectionError(
      error,
      tableUpdateData,
      tableData,
      syncedTableDataMap,
      ID_COLUMN_INDEX
    );

    if (tableDataIndex !== undefined) {
      mergedErrors.push({
        rowIndex: tableDataIndex,
        field: error.field,
      });
    }
  });

  // Map create section errors: these are new rows, find them in syncedTableData
  // IMPORTANT: Use the createRowIndexMap to directly map errors from tableCreateNewData index to syncedTableData index
  // This handles both matched rows (synced in place) and unmatched rows (added at the end)
  createSectionErrors.forEach((error) => {
    const createRow = tableCreateNewData[error.rowIndex];
    if (createRow) {
      const tableDataIndex = mapCreateSectionError(
        error,
        createRow,
        createRowIndexMap,
        syncedTableData,
        ID_COLUMN_INDEX
      );

      if (tableDataIndex !== undefined) {
        mergedErrors.push({
          rowIndex: tableDataIndex,
          field: error.field,
        });
      }
    }
  });

  // Merge with existing errors (avoid duplicates)
  const existingErrorIndices = new Set(mergedErrors.map((e) => e.rowIndex));
  rowsWithErrors.forEach((error) => {
    if (!existingErrorIndices.has(error.rowIndex)) {
      mergedErrors.push(error);
    }
  });

  return mergedErrors;
};

/**
 * Sync data from confirm view back to detail table
 * Matches rows by ID for updated rows, and by content similarity for create rows
 * This ensures ALL columns of ALL modified rows are synced, including changes made in confirm page
 *
 * @param tableData - Current table data in edit view
 * @param tableUpdateData - Updated rows from confirm view (pairs: [original, updated, ...])
 * @param tableCreateNewData - New rows from confirm view
 * @param updateSectionErrors - Validation errors from update section
 * @param createSectionErrors - Validation errors from create section
 * @param rowsWithErrors - Existing validation errors
 * @returns Object containing synced table data, merged errors, and change flag
 */
export const syncDataBackToDetailHansontable = (
  tableData: PlanTableData,
  tableUpdateData: PlanTableData,
  tableCreateNewData: PlanTableData,
  updateSectionErrors: { rowIndex: number; field: { index: number; message: string }[] }[],
  createSectionErrors: { rowIndex: number; field: { index: number; message: string }[] }[],
  rowsWithErrors: { rowIndex: number; field: { index: number; message: string }[] }[]
): {
  syncedTableData: PlanTableData;
  mergedErrors: { rowIndex: number; field: { index: number; message: string }[] }[];
  hasDataChanged: boolean;
} => {
  const ID_COLUMN_INDEX = 23;

  // Check if there are any changes in confirm view tables
  const hasUpdateChanges = tableUpdateData.length > 0;
  const hasCreateChanges = tableCreateNewData.length > 0;

  if (!hasUpdateChanges && !hasCreateChanges) {
    // No changes in confirm view, return original data
    return {
      syncedTableData: tableData.map((row) => [...row]),
      mergedErrors: [],
      hasDataChanged: false,
    };
  }

  // Create a copy of tableData to modify
  const syncedTableData = tableData.map((row) => [...row]);
  let hasDataChanged = false;

  // Build map of updated rows by ID
  const updatedRowsMap = buildUpdatedRowsMap(tableUpdateData, tableData, ID_COLUMN_INDEX);

  // First pass: Sync updated rows (match by ID)
  const updatedRowsChanged = syncUpdatedRows(syncedTableData, updatedRowsMap, ID_COLUMN_INDEX);
  hasDataChanged = hasDataChanged || updatedRowsChanged;

  // Second pass: Sync create rows
  const {
    matchedCreateRowIndices,
    createRowIndexMap,
    hasDataChanged: createRowsChanged,
  } = syncCreateRows(syncedTableData, tableCreateNewData, ID_COLUMN_INDEX);
  hasDataChanged = hasDataChanged || createRowsChanged;

  // Third pass: Add any additional create rows that might have been added in confirm view
  // IMPORTANT: Pass createRowIndexMap so unmatched rows can be mapped for error handling
  const unmatchedRowsChanged = addUnmatchedCreateRows(
    syncedTableData,
    tableCreateNewData,
    matchedCreateRowIndices,
    createRowIndexMap
  );
  hasDataChanged = hasDataChanged || unmatchedRowsChanged;

  // Merge confirm errors back to main errors
  // IMPORTANT: Pass createRowIndexMap to mergeErrors so it can correctly map errors
  // for both matched and unmatched create rows
  const mergedErrors = mergeErrors({
    syncedTableData,
    tableData,
    tableUpdateData,
    tableCreateNewData,
    updateSectionErrors,
    createSectionErrors,
    rowsWithErrors,
    createRowIndexMap,
    ID_COLUMN_INDEX,
  });

  return {
    syncedTableData,
    mergedErrors,
    hasDataChanged,
  };
};

/**
 * Create a row with metadata preserved from a source row
 *
 * @param dataRow - Row data from Handsontable (23 columns)
 * @param sourceRow - Source row to get metadata from (26 columns)
 * @returns Row with 26 columns (23 from dataRow + 3 metadata columns)
 */
const createRowWithMetadata = (dataRow: PlanTableRow, sourceRow: PlanTableRow): PlanTableRow => {
  return [
    ...dataRow, // First 23 columns from Handsontable
    sourceRow[23] || '', // id
    sourceRow[24] || '0', // isDeleted
    sourceRow[25] || '0', // isArchive
  ];
};

/**
 * Preserve metadata for original row in update pair
 *
 * @param originalRow - Original row from Handsontable
 * @param currentOriginalRow - Current original row in tableData
 * @param pairIndex - Index of the pair in tableData
 * @param updatedTableData - Table data array to update
 */
const preserveOriginalRowMetadata = (
  originalRow: PlanTableRow,
  currentOriginalRow: PlanTableRow | undefined,
  pairIndex: number,
  updatedTableData: PlanTableData
): void => {
  if (originalRow.length >= 26) {
    // Already has full 26 columns, use as is
    updatedTableData[pairIndex] = [...originalRow];
  } else if (originalRow.length === 23 && currentOriginalRow && currentOriginalRow.length >= 26) {
    // If original row has only 23 columns, preserve metadata from current tableData
    updatedTableData[pairIndex] = createRowWithMetadata(originalRow, currentOriginalRow);
  } else {
    // No metadata to preserve, use row as is
    updatedTableData[pairIndex] = [...originalRow];
  }
};

/**
 * Preserve metadata for updated row in update pair
 *
 * @param updatedRow - Updated row from Handsontable
 * @param currentUpdatedRow - Current updated row in tableData
 * @param currentOriginalRow - Current original row in tableData
 * @param pairIndex - Index of the updated row in tableData
 * @param updatedTableData - Table data array to update
 */
const preserveUpdatedRowMetadata = (
  updatedRow: PlanTableRow,
  currentUpdatedRow: PlanTableRow | undefined,
  currentOriginalRow: PlanTableRow | undefined,
  pairIndex: number,
  updatedTableData: PlanTableData
): void => {
  if (updatedRow.length >= 26) {
    // Already has 26 columns, use as is
    updatedTableData[pairIndex] = [...updatedRow];
  } else if (updatedRow.length === 23) {
    // Handsontable returned only 23 columns, preserve metadata
    if (currentUpdatedRow && currentUpdatedRow.length >= 26) {
      // Preserve metadata from current updated row
      updatedTableData[pairIndex] = createRowWithMetadata(updatedRow, currentUpdatedRow);
    } else if (currentOriginalRow && currentOriginalRow.length >= 26) {
      // If current row doesn't have metadata, try to get from original row
      updatedTableData[pairIndex] = createRowWithMetadata(updatedRow, currentOriginalRow);
    } else {
      // No metadata available, use row as is
      updatedTableData[pairIndex] = [...updatedRow];
    }
  }
};

/**
 * Update tableData with new data from Handsontable while preserving metadata
 * IMPORTANT: Handsontable returns only 23 columns, need to preserve metadata (id, isDeleted, isArchive)
 *
 * @param tableData - Current table data array
 * @param data - New data from Handsontable (pair: [originalRow, updatedRow])
 * @param index - Index of the pair in tableData (pair index, not row index)
 * @returns Updated table data array with preserved metadata
 */
export const updateTableDataWithMetadata = (
  tableData: PlanTableData,
  data: PlanTableData,
  index: number
): PlanTableData => {
  // Update the specific pair in tableData
  // IMPORTANT: Handsontable returns only 23 columns, need to preserve metadata (id, isDeleted, isArchive)
  const updatedTableData = [...tableData];
  const pairIndex = index * 2;
  const originalRowIndex = pairIndex;
  const updatedRowIndex = pairIndex + 1;

  // Preserve original row (should already have 26 columns)
  if (data[0]) {
    const currentOriginalRow = tableData[originalRowIndex];
    preserveOriginalRowMetadata(data[0], currentOriginalRow, originalRowIndex, updatedTableData);
  }

  // Preserve updated row metadata (id, isDeleted, isArchive)
  if (data[1]) {
    const currentUpdatedRow = tableData[updatedRowIndex];
    const currentOriginalRow = tableData[originalRowIndex];
    preserveUpdatedRowMetadata(
      data[1],
      currentUpdatedRow,
      currentOriginalRow,
      updatedRowIndex,
      updatedTableData
    );
  }

  return updatedTableData;
};

// =============================================================================
// Convert Plans for API Request
// =============================================================================

/**
 * Create a map of original planDeliveries by id to get displayOrder
 */
export const createOriginalPlansMap = (
  planDetail: PropertyPlansApiResponse | null
): Map<string, OriginalPlanMapEntry> => {
  const originalPlansMap = new Map<string, OriginalPlanMapEntry>();
  planDetail?.planDeliveries.forEach((plan, index) => {
    if (plan.id) {
      // Try to get displayOrder from plan (might exist in API response even if not in type)
      const displayOrder = (plan as PlanDeliveryWithDisplayOrder).displayOrder;
      originalPlansMap.set(plan.id, {
        index,
        displayOrder: displayOrder !== undefined ? displayOrder : index + 1, // Use index + 1 as fallback
        plan,
      });
    }
  });
  return originalPlansMap;
};

/**
 * Convert update plans from table data to UpdatePlanRequest format
 */
export const convertUpdatePlans = (
  tableUpdateData: PlanTableData,
  plantOptions: SearchOption[],
  witnessCompanyOptions: SearchOption[],
  originalPlansMap: Map<string, OriginalPlanMapEntry>
): UpdatePlanRequest[] => {
  const ID_COLUMN_INDEX = 23;
  const PLANT_COLUMN_INDEX = 1;
  const WITNESS_COMPANY_COLUMN_INDEX = 18;
  const updatePlans: UpdatePlanRequest[] = [];

  // Process update data: only convert updated rows (odd indices)
  const updateRowsToConvert = tableUpdateData.filter((_, index) => index % 2 === 1);
  if (updateRowsToConvert.length === 0) {
    return updatePlans;
  }

  const convertedPlans = convertFromTableData(
    updateRowsToConvert,
    plantOptions,
    witnessCompanyOptions
  );

  const normalizeCellValue = (value: unknown): string => {
    return value != null ? String(value).trim() : '';
  };

  convertedPlans.forEach((plan: CreatePlanRequest, index: number) => {
    const pairStartIndex = index * 2;
    const originalRow = tableUpdateData[pairStartIndex];
    const updatedRow = tableUpdateData[pairStartIndex + 1];
    const rowId = getCellString(updatedRow, ID_COLUMN_INDEX);

    if (rowId !== '') {
      const originalPlanData = originalPlansMap.get(rowId);
      const hasPlantChanged =
        normalizeCellValue(originalRow?.[PLANT_COLUMN_INDEX]) !==
        normalizeCellValue(updatedRow?.[PLANT_COLUMN_INDEX]);
      const hasWitnessCompanyChanged =
        normalizeCellValue(originalRow?.[WITNESS_COMPANY_COLUMN_INDEX]) !==
        normalizeCellValue(updatedRow?.[WITNESS_COMPANY_COLUMN_INDEX]);

      let normalizedPlan = plan;
      if (!hasPlantChanged && !normalizedPlan.customerSupplierId) {
        const originalCustomerSupplierId =
          originalPlanData?.plan.customerSupplierId ??
          originalPlanData?.plan.customerSupplier?.id ??
          null;
        normalizedPlan = {
          ...normalizedPlan,
          customerSupplierId: originalCustomerSupplierId,
        };
      }

      if (!hasWitnessCompanyChanged && !normalizedPlan.witnessCompanyId) {
        const originalWitnessCompanyId =
          originalPlanData?.plan.witnessCompanyId ??
          originalPlanData?.plan.witnessCompany?.id ??
          null;
        normalizedPlan = {
          ...normalizedPlan,
          witnessCompanyId: originalWitnessCompanyId,
        };
      }

      // Remove id from plan (may be null) - omit it instead of destructuring
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { id, ...planWithoutId } = normalizedPlan;
      const updateRequest: UpdatePlanRequest = {
        ...planWithoutId, // Spread all fields from CreatePlanRequest (without id)
        id: rowId,
        displayOrder: originalPlanData?.displayOrder,
      };
      updatePlans.push(updateRequest);
    }
  });

  return updatePlans;
};

/**
 * Convert create plans from table data to UpdatePlanRequest format
 */
export const convertCreatePlans = (
  tableCreateNewData: PlanTableData,
  plantOptions: SearchOption[],
  witnessCompanyOptions: SearchOption[]
): UpdatePlanRequest[] => {
  const createPlans: UpdatePlanRequest[] = [];

  if (tableCreateNewData.length === 0) {
    return createPlans;
  }

  const convertedPlans = convertFromTableData(
    tableCreateNewData,
    plantOptions,
    witnessCompanyOptions
  );

  convertedPlans.forEach((plan: CreatePlanRequest) => {
    // Remove id from plan (may be null) - omit it instead of destructuring
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { id, ...planWithoutId } = plan;
    const createRequest: UpdatePlanRequest = {
      ...planWithoutId, // Spread all fields from CreatePlanRequest (without id)
      // No id for new records
    };
    createPlans.push(createRequest);
  });

  return createPlans;
};

/**
 * Convert delete plans from table data to UpdatePlanRequest format
 * For delete operations, API requires all required fields even when isDeleted is true
 */
export const convertDeletePlans = (
  tableDeletedData: PlanTableData,
  plantOptions: SearchOption[],
  witnessCompanyOptions: SearchOption[],
  originalPlansMap: Map<string, OriginalPlanMapEntry>
): UpdatePlanRequest[] => {
  const ID_COLUMN_INDEX = 23;
  const deletePlans: UpdatePlanRequest[] = [];

  if (tableDeletedData.length === 0) {
    return deletePlans;
  }

  // Convert table data to CreatePlanRequest to get all fields
  const convertedPlans = convertFromTableData(
    tableDeletedData,
    plantOptions,
    witnessCompanyOptions
  );

  convertedPlans.forEach((plan: CreatePlanRequest, index: number) => {
    const deletedRow = tableDeletedData[index];
    const rowId = getCellString(deletedRow, ID_COLUMN_INDEX);

    if (rowId !== '') {
      const originalPlanData = originalPlansMap.get(rowId);
      // Remove id from plan (may be null) - omit it instead of destructuring
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { id, ...planWithoutId } = plan;
      const deleteRequest: UpdatePlanRequest = {
        ...planWithoutId, // Spread all fields from CreatePlanRequest (without id)
        id: rowId,
        isDeleted: true,
        displayOrder: originalPlanData?.displayOrder,
      };
      deletePlans.push(deleteRequest);
    }
  });

  return deletePlans;
};
